import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { api } from '@/lib/api'
import { formatNumber, formatBytes } from '@/lib/utils'
import {
  Users,
  Tv,
  Activity,
  HardDrive,
  Clock,
  AlertCircle,
  ChevronRight,
  ChevronDown,
  Zap,
  Play,
  Square,
  RefreshCw,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  XCircle,
} from 'lucide-react'

interface Stats {
  total_users: number
  active_users: number
  total_streams: number
  active_streams: number
  total_sessions: number
  total_bandwidth: number
  uptime: number
}

function StatCard({
  title,
  value,
  icon: Icon,
  description,
}: {
  title: string
  value: string | number
  icon: React.ElementType
  description?: string
}) {
  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-muted-foreground">
            {title}
          </p>
          <p className="mt-2 text-3xl font-semibold text-foreground">
            {value}
          </p>
          {description && (
            <p className="mt-1 text-sm text-muted-foreground">
              {description}
            </p>
          )}
        </div>
        <div className="p-3 bg-primary/10 rounded-lg">
          <Icon className="w-6 h-6 text-primary" />
        </div>
      </div>
    </div>
  )
}

interface Viewer {
  ip: string
  username: string
  user_agent?: string
  client_type: string
  duration: string
}

// Map client types to display names and icons
const clientTypeDisplay: Record<string, { name: string; icon: string }> = {
  'obacast-android': { name: 'OBACast', icon: '📱' },
  'fire-tv': { name: 'Fire TV', icon: '🔥' },
  'nvidia-shield': { name: 'Shield', icon: '🛡️' },
  'chromecast': { name: 'Chromecast', icon: '📺' },
  'android-tv': { name: 'Android TV', icon: '📺' },
  'android': { name: 'Android', icon: '🤖' },
  'apple-tv': { name: 'Apple TV', icon: '🍎' },
  'iphone': { name: 'iPhone', icon: '📱' },
  'ipad': { name: 'iPad', icon: '📱' },
  'vlc': { name: 'VLC', icon: '🎬' },
  'kodi': { name: 'Kodi', icon: '🎬' },
  'emby': { name: 'Emby', icon: '🎬' },
  'jellyfin': { name: 'Jellyfin', icon: '🎬' },
  'plex': { name: 'Plex', icon: '🎬' },
  'tivimate': { name: 'TiviMate', icon: '📺' },
  'ibo-player': { name: 'iBO', icon: '📺' },
  'smarters': { name: 'Smarters', icon: '📺' },
  'perfect-player': { name: 'Perfect', icon: '📺' },
  'ott-navigator': { name: 'OTT Nav', icon: '📺' },
  'xciptv': { name: 'XCIPTV', icon: '📺' },
  'chrome': { name: 'Chrome', icon: '🌐' },
  'firefox': { name: 'Firefox', icon: '🦊' },
  'safari': { name: 'Safari', icon: '🧭' },
  'edge': { name: 'Edge', icon: '🌐' },
  'roku': { name: 'Roku', icon: '📺' },
  'samsung-tv': { name: 'Samsung', icon: '📺' },
  'lg-tv': { name: 'LG TV', icon: '📺' },
  'unknown': { name: 'Unknown', icon: '❓' },
}

interface ActiveStream {
  id: string
  name: string
  type: string
  startup_mode: string
  viewer_count: number
  viewers: Viewer[]
  is_online: boolean
  started_at?: string
  // Source info
  source_domain?: string
  // Stream health metrics
  connection_resets: number
  dts_errors: number
  speed_drops: number
  segment_discontinuities: number
  segment_duration_issues: number
  health_score: number
  health_status: string
}

function HealthBadge({ stream }: { stream: ActiveStream }) {
  const status = stream.health_status || 'healthy'
  const score = stream.health_score || 100

  // Build tooltip with details
  const details = []
  if (stream.connection_resets > 0) details.push(`${stream.connection_resets} resets`)
  if (stream.dts_errors > 0) details.push(`${stream.dts_errors} DTS errors`)
  if (stream.speed_drops > 0) details.push(`${stream.speed_drops} speed drops`)
  if (stream.segment_discontinuities > 0) details.push(`${stream.segment_discontinuities} discontinuities`)
  if (stream.segment_duration_issues > 0) details.push(`${stream.segment_duration_issues} bad segments`)
  const tooltip = details.length > 0
    ? `Health: ${Math.round(score)}% - ${details.join(', ')}`
    : `Health: ${Math.round(score)}%`

  if (status === 'critical') {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-red-500/20 text-red-600 dark:text-red-400 rounded-full"
        title={tooltip}
      >
        <XCircle className="w-3 h-3" />
        {Math.round(score)}%
      </span>
    )
  }

  if (status === 'warning') {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-yellow-500/20 text-yellow-600 dark:text-yellow-400 rounded-full"
        title={tooltip}
      >
        <AlertTriangle className="w-3 h-3" />
        {Math.round(score)}%
      </span>
    )
  }

  // Healthy - show green badge with score
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-green-500/20 text-green-600 dark:text-green-400 rounded-full"
      title={tooltip}
    >
      <CheckCircle2 className="w-3 h-3" />
      {Math.round(score)}%
    </span>
  )
}

export default function Dashboard() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [expandedStream, setExpandedStream] = useState<string | null>(null)
  const [controllingStream, setControllingStream] = useState<string | null>(null)

  const { data: stats, isLoading, error } = useQuery<Stats>({
    queryKey: ['dashboard-stats'],
    queryFn: () => api.getDashboardStats(),
    refetchInterval: 30000,
  })

  const { data: activeStreams } = useQuery({
    queryKey: ['active-streams'],
    queryFn: () => api.getActiveStreams(),
    refetchInterval: 10000,
  })

  // Sort active streams by name for consistent ordering
  const sortedActiveStreams = activeStreams?.streams
    ? [...activeStreams.streams].sort((a: any, b: any) => a.name.localeCompare(b.name))
    : []

  const { data: activeSessions } = useQuery({
    queryKey: ['active-sessions'],
    queryFn: () => api.getActiveSessions(),
    refetchInterval: 10000,
  })

  // Stream control mutations
  const stopStreamMutation = useMutation({
    mutationFn: (streamId: string) => api.stopTestStream(streamId),
    onMutate: (streamId) => setControllingStream(streamId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['active-streams'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] })
    },
    onSettled: () => setControllingStream(null),
  })

  const restartStreamMutation = useMutation({
    mutationFn: (streamId: string) => api.testStream(streamId),
    onMutate: (streamId) => setControllingStream(streamId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['active-streams'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] })
    },
    onSettled: () => setControllingStream(null),
  })

  const handleStopStream = (e: React.MouseEvent, streamId: string) => {
    e.stopPropagation()
    if (confirm('Stop this stream? Viewers will be disconnected.')) {
      stopStreamMutation.mutate(streamId)
    }
  }

  const handleRestartStream = (e: React.MouseEvent, streamId: string) => {
    e.stopPropagation()
    restartStreamMutation.mutate(streamId)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64 text-destructive">
        <AlertCircle className="w-5 h-5 mr-2" />
        Failed to load dashboard data
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <h1 className="text-3xl font-semibold text-foreground">
        Dashboard
      </h1>

      {/* Stats Grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Users"
          value={formatNumber(stats?.total_users || 0)}
          icon={Users}
          description={`${stats?.active_users || 0} active`}
        />
        <StatCard
          title="Total Streams"
          value={formatNumber(stats?.total_streams || 0)}
          icon={Tv}
          description={`${stats?.active_streams || 0} online`}
        />
        <StatCard
          title="Active Sessions"
          value={formatNumber(stats?.total_sessions || 0)}
          icon={Activity}
        />
        <StatCard
          title="Bandwidth"
          value={formatBytes(stats?.total_bandwidth || 0)}
          icon={HardDrive}
          description="Current usage"
        />
      </div>

      {/* Active Streams & Sessions */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Active Streams */}
        <div className="bg-card rounded-lg border border-border">
          <div className="px-6 py-4 border-b border-border">
            <h2 className="text-lg font-semibold text-foreground">
              Active Streams
            </h2>
          </div>
          <div className="divide-y divide-border">
            {sortedActiveStreams.length ? (
              sortedActiveStreams.slice(0, 10).map((stream: ActiveStream) => (
                <div key={stream.id} className="border-b border-border last:border-b-0">
                  <div className="flex items-center">
                    {/* Expand button */}
                    <button
                      onClick={() => setExpandedStream(expandedStream === stream.id ? null : stream.id)}
                      className="px-3 py-4 hover:bg-muted/50 transition-colors"
                      title={stream.viewers?.length ? "Show viewers" : "No viewers"}
                    >
                      {expandedStream === stream.id ? (
                        <ChevronDown className="w-4 h-4 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      )}
                    </button>
                    {/* Main stream info - clickable to edit */}
                    <button
                      onClick={() => navigate(`/streams?edit=${stream.id}`)}
                      className="flex-1 min-w-0 px-3 py-4 flex items-center justify-between gap-2 hover:bg-muted/50 transition-colors text-left"
                    >
                      <div className="min-w-0">
                        <p className="text-base font-medium text-foreground truncate">
                          {stream.name}
                        </p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className="text-sm text-muted-foreground">
                            {stream.viewer_count} {stream.viewer_count === 1 ? 'viewer' : 'viewers'}
                          </span>
                          {stream.viewers?.length > 0 && (
                            <span className="hidden sm:inline text-xs text-muted-foreground">
                              ({stream.viewers.map(v => v.ip.split(':')[0]).filter((ip, i, arr) => arr.indexOf(ip) === i).length} unique IPs)
                            </span>
                          )}
                          {stream.source_domain && (
                            <span className="hidden sm:inline text-xs text-muted-foreground font-mono">
                              • {stream.source_domain}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {stream.startup_mode === 'always_on' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-purple-500/20 text-purple-600 dark:text-purple-400 rounded-full">
                            <Zap className="w-3 h-3" />
                            24/7
                          </span>
                        ) : (
                          <span className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-blue-500/20 text-blue-600 dark:text-blue-400 rounded-full">
                            <Play className="w-3 h-3" />
                            On-Demand
                          </span>
                        )}
                        <HealthBadge stream={stream} />
                        <span className="badge-success">
                          Online
                        </span>
                      </div>
                    </button>
                    {/* Stream control buttons */}
                    <div className="flex items-center gap-1 pr-3">
                      <button
                        onClick={(e) => handleRestartStream(e, stream.id)}
                        disabled={controllingStream === stream.id}
                        className="p-2 rounded-lg hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                        title="Restart stream"
                      >
                        {controllingStream === stream.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <RefreshCw className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        onClick={(e) => handleStopStream(e, stream.id)}
                        disabled={controllingStream === stream.id}
                        className="p-2 rounded-lg hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors disabled:opacity-50"
                        title="Stop stream"
                      >
                        <Square className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  {/* Expanded viewer list */}
                  {expandedStream === stream.id && stream.viewers?.length > 0 && (
                    <div className="px-6 pb-4 bg-muted/30">
                      <div className="text-xs font-medium text-muted-foreground mb-2 pt-2">
                        Connected Viewers
                      </div>
                      <div className="space-y-1">
                        {stream.viewers.map((viewer, idx) => {
                          const clientInfo = clientTypeDisplay[viewer.client_type] || clientTypeDisplay['unknown']
                          return (
                            <div key={idx} className="flex items-center justify-between text-sm py-1.5 px-2 rounded bg-background gap-2">
                              <div className="flex items-center gap-2 min-w-0">
                                <span
                                  className="text-xs px-1.5 py-0.5 rounded shrink-0"
                                  style={{
                                    backgroundColor: viewer.client_type === 'obacast-android' ? 'rgb(34 197 94 / 0.2)' : 'rgb(var(--muted))',
                                    color: viewer.client_type === 'obacast-android' ? 'rgb(34 197 94)' : 'inherit'
                                  }}
                                  title={viewer.user_agent || viewer.client_type}
                                >
                                  {clientInfo.icon} {clientInfo.name}
                                </span>
                                <span className="text-foreground truncate">{viewer.username}</span>
                                <span className="font-mono text-xs text-muted-foreground shrink-0">
                                  {viewer.ip.split(':')[0]}
                                </span>
                              </div>
                              <span className="text-xs text-muted-foreground shrink-0">
                                {viewer.duration}
                              </span>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}
                  {expandedStream === stream.id && (!stream.viewers || stream.viewers.length === 0) && (
                    <div className="px-6 pb-4 bg-muted/30">
                      <p className="text-sm text-muted-foreground py-2">No active viewers</p>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="px-6 py-8 text-center text-muted-foreground">
                No active streams
              </div>
            )}
          </div>
        </div>

        {/* Active Sessions */}
        <div className="bg-card rounded-lg border border-border">
          <div className="px-6 py-4 border-b border-border">
            <h2 className="text-lg font-semibold text-foreground">
              Recent Sessions
            </h2>
          </div>
          <div className="divide-y divide-border">
            {activeSessions?.sessions?.length ? (
              activeSessions.sessions.slice(0, 5).map((session: any) => (
                <div key={session.id} className="px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-base font-medium text-foreground truncate">
                      {session.username}
                    </p>
                    <p className="text-sm text-muted-foreground truncate">
                      {session.ip} - {session.stream_name}
                    </p>
                  </div>
                  <div className="flex items-center text-sm text-muted-foreground shrink-0">
                    <Clock className="w-4 h-4 mr-1" />
                    {session.duration}
                  </div>
                </div>
              ))
            ) : (
              <div className="px-6 py-8 text-center text-muted-foreground">
                No active sessions
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
